/**
 * A line on a project's roster (spec §4.2).
 *
 * One row per person being paid. A line is either a creator — linked to an
 * influencer, who signs in and raises their own invoice — or a vendor: an
 * editor, a boosting spend, a shoot cost, invoiced on their behalf. The sheet
 * this replaces already mixes the two in one table, so they share a row shape,
 * a payment run and a tracker.
 *
 * Pure. State is derived rather than stored, because an invoice already has a
 * status machine and two of them over one record will drift.
 */

export const CREATOR = 'creator';
export const VENDOR = 'vendor';

export const LINE_STATES = {
  ADDED: 'added',
  LOCKED: 'locked',
  READY: 'ready',
  INVOICED: 'invoiced',
  SETTLED: 'settled',
};

export const LINE_STATE_LABELS = {
  [LINE_STATES.ADDED]: 'Added',
  [LINE_STATES.LOCKED]: 'Price locked',
  [LINE_STATES.READY]: 'Ready to invoice',
  [LINE_STATES.INVOICED]: 'Invoiced',
  [LINE_STATES.SETTLED]: 'Settled',
};

const filled = (value) => String(value ?? '').trim() !== '';

function money(value) {
  if (!filled(value)) return null;
  const parsed = typeof value === 'number' ? value : parseFloat(String(value).trim());
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * The line's kind. `kind` is authoritative once the API sends it; until then
 * a row carrying a vendor name and no influencer is a vendor, and everything
 * else is a creator — which is what every existing row is.
 */
export function lineKind(row) {
  const line = row || {};
  if (line.kind === VENDOR || line.kind === CREATOR) return line.kind;
  if (filled(line.vendor_name) && !line.influencer_id && !filled(line.influencer_name)) {
    return VENDOR;
  }
  return CREATOR;
}

export function isVendor(row) {
  return lineKind(row) === VENDOR;
}

/** `{field: message}`; empty means valid. */
export function validateLine(form) {
  const values = form || {};
  const kind = lineKind(values);
  const errors = {};

  if (kind === VENDOR) {
    if (!filled(values.vendor_name)) {
      errors.vendor_name = "Enter the vendor's name, e.g. the editor or the boosting platform.";
    }
    // A row that is both would be two things at once, and the invoice would
    // not know who it belongs to.
    if (values.influencer_id) {
      errors.influencer_id = 'A vendor line cannot also be a creator. Add them as a creator line.';
    }
    // There is no vendor on the backend: every roster line is a
    // ShortlistedInfluencer with an Influencer behind it, and the create
    // endpoint needs an influencer_id or a name and email. `linePayload`
    // sends neither for a vendor, so this always failed — as a 400 naming a
    // field the vendor form does not show. The toggle is disabled upstream;
    // this refuses it at the boundary rather than leaving the server to.
    if (filled(values.vendor_name)) {
      errors.vendor_name =
        'Vendor lines are not supported by the API yet. Add them as a new creator '
        + 'with a name and email — they are paid through the same run.';
    }
  } else if (!values.influencer_id && !(filled(values.name) && filled(values.email))) {
    errors.influencer_id = 'Pick a creator from the list, or enter a name and email for a new one.';
  }

  const cost = values.internal_cost;
  if (filled(cost)) {
    const parsed = money(cost);
    if (parsed === null || parsed <= 0) {
      errors.internal_cost = 'Enter the agreed cost as a number.';
    }
  } else if (values.locked) {
    // Locking is a promise about a figure; there has to be one.
    errors.internal_cost = 'Enter the agreed cost before locking the price.';
  }

  return errors;
}

/**
 * The request body. Costs map onto the fields the workspace endpoint already
 * uses: `influencer_final_price` is what we pay, `final_price` is what the
 * client is billed for this line.
 */
export function linePayload(form) {
  const values = form || {};
  const kind = lineKind(values);
  const payload = { kind };

  if (kind === VENDOR) {
    payload.vendor_name = String(values.vendor_name || '').trim();
  } else if (values.influencer_id) {
    payload.influencer_id = values.influencer_id;
  } else {
    payload.name = String(values.name || '').trim();
    payload.email = String(values.email || '').trim();
  }

  const payout = money(values.internal_cost);
  const billing = money(values.client_cost);
  if (payout !== null) payload.influencer_final_price = payout;
  if (billing !== null) payload.final_price = billing;

  ['deliverables', 'managed_by', 'vendor_name', 'internal_poc'].forEach((field) => {
    if (field === 'vendor_name' && kind === VENDOR) return; // already set above
    if (filled(values[field])) payload[field] = String(values[field]).trim();
  });

  if (values.locked !== undefined) payload.locked = Boolean(values.locked) && payout !== null;

  return payload;
}

/**
 * Where the line has got to. Derived in this order because each step depends
 * on the one before it; a vendor line has no payout step to wait on, since
 * nobody signs in for it.
 */
export function lineState(row) {
  const line = row || {};
  const status = line.invoice_status;

  if (status === 'paid') return LINE_STATES.SETTLED;
  // A rejected invoice is still an invoice: the line is not settled, and it is
  // past the point of waiting for one.
  if (filled(status)) return LINE_STATES.INVOICED;
  if (!line.price_locked) return LINE_STATES.ADDED;
  if (isVendor(line) || line.payout_details_on_file) return LINE_STATES.READY;
  return LINE_STATES.LOCKED;
}
